// src/services/eventDocumentsService.js
// Registro documental por evento: anota cada documento emitido (con su numero
// correlativo) y aloja los ficheros que devuelve el cliente (contrato firmado,
// justificante de la transferencia de la senal).
//
// Ver migracion supabase/migrations/20260921_event_documents.sql

import { getSupabaseClient } from "./supabaseClient.js";

const TABLE = "event_documents";
const BUCKET = "signed-documents";

// Tipos de documento que maneja el flujo de confirmacion.
export const DOC_KINDS = Object.freeze({
    CONTRATO: "contrato",
    JUSTIFICANTE_SENAL: "justificante_senal",
    RECIBO_SENAL: "recibo_senal",
});

export const DOC_KIND_LABELS = Object.freeze({
    [DOC_KINDS.CONTRATO]: "Contrato firmado",
    [DOC_KINDS.JUSTIFICANTE_SENAL]: "Justificante de la señal",
    [DOC_KINDS.RECIBO_SENAL]: "Recibo de la señal",
});

// Los dos requisitos que, una vez cubiertos, permiten confirmar el evento.
export const CONFIRMATION_REQUIREMENTS = Object.freeze([
    DOC_KINDS.CONTRATO,
    DOC_KINDS.JUSTIFICANTE_SENAL,
]);

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic"];

// ─── Lectura ──────────────────────────────────────────────────────────────────

/**
 * Lista los documentos de un evento, el mas reciente primero.
 */
export async function listEventDocuments(eventId) {
    if (!eventId) return [];
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from(TABLE)
        .select("*")
        .eq("event_id", eventId)
        .order("created_at", { ascending: false });

    if (error) {
        console.error("[eventDocuments] listEventDocuments:", error);
        return [];
    }
    return data || [];
}

/**
 * Estado de los requisitos para confirmar el evento.
 * Devuelve { ok, items: [{ kind, label, done, doc }] }
 */
export function getConfirmationStatus(documents = []) {
    const items = CONFIRMATION_REQUIREMENTS.map((kind) => {
        // Solo cuenta si hay fichero subido: una fila de contrato 'emitido'
        // sin adjunto significa que se genero pero no ha vuelto firmado.
        const doc = documents.find((d) => d.kind === kind && !!d.file_path);
        return { kind, label: DOC_KIND_LABELS[kind], done: !!doc, doc: doc || null };
    });

    return { ok: items.every((i) => i.done), items };
}

// ─── Emision ──────────────────────────────────────────────────────────────────

/**
 * Anota un documento recien generado. Se llama justo despues de consumir el
 * numero correlativo, para que ningun numero quede sin rastro.
 *
 * No lanza: un fallo al registrar no debe impedir que el usuario se descargue
 * el PDF que ya tiene delante.
 */
export async function registerIssuedDocument({ teamId, eventId, kind, docType, docNumber, userId = null }) {
    if (!teamId || !eventId || !docNumber) return null;
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from(TABLE)
        .insert({
            team_id: teamId,
            event_id: eventId,
            kind,
            origin: "emitido",
            status: "emitido",
            doc_number: docNumber,
            doc_type: docType,
            issued_at: new Date().toISOString(),
            issued_by: userId,
        })
        .select()
        .single();

    if (error) {
        console.error("[eventDocuments] registerIssuedDocument:", error);
        return null;
    }
    return data;
}

// ─── Subida de ficheros ───────────────────────────────────────────────────────

function assertFile(file) {
    if (!file) throw new Error("No se ha seleccionado ningún archivo");
    if (file.size > MAX_FILE_BYTES) {
        throw new Error(`El archivo supera el límite de ${MAX_FILE_BYTES / 1024 / 1024} MB`);
    }
    if (file.type && !ALLOWED_TYPES.includes(file.type)) {
        throw new Error("Formato no admitido. Sube un PDF o una imagen");
    }
}

function buildPath(eventId, kind, file) {
    const safeName = (file.name || "documento").replace(/[^a-zA-Z0-9.\-_]/g, "_");
    return `${eventId}/${kind}_${Date.now()}_${safeName}`;
}

/**
 * Sube el contrato firmado y lo engancha a la fila del documento emitido, de
 * modo que el fichero firmado conserva el numero correlativo original.
 *
 * Si no existe fila previa (contrato generado antes de que existiera este
 * registro), se crea una con el numero que indique el usuario.
 */
export async function uploadSignedContract({ teamId, eventId, file, documentId = null, docNumber = null, userId = null }) {
    assertFile(file);
    const supabase = getSupabaseClient();

    const path = buildPath(eventId, DOC_KINDS.CONTRATO, file);
    const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });

    if (upErr) {
        console.error("[eventDocuments] uploadSignedContract:", upErr);
        throw new Error("Error subiendo el archivo");
    }

    const patch = {
        status: "firmado",
        file_path: path,
        file_name: file.name || null,
        file_size: file.size || null,
        uploaded_at: new Date().toISOString(),
        uploaded_by: userId,
    };

    if (documentId) {
        const { data, error } = await supabase
            .from(TABLE).update(patch).eq("id", documentId).select().single();
        if (error) {
            // El fichero ya esta arriba: si no podemos enlazarlo, lo retiramos
            // para no dejar huerfanos en el bucket.
            await supabase.storage.from(BUCKET).remove([path]);
            throw error;
        }
        return data;
    }

    // Sin fila previa el contrato no consta emitido desde el CRM (firmado por
    // otra via, anterior a este registro, o rescatado de un correo). Se anota
    // como recibido: sin numero correlativo, porque no se llego a consumir
    // ninguno. Marcarlo 'emitido' sin numero incumple la regla de la tabla.
    const isTracked = !!docNumber;

    const { data, error } = await supabase
        .from(TABLE)
        .insert({
            team_id: teamId,
            event_id: eventId,
            kind: DOC_KINDS.CONTRATO,
            origin: isTracked ? "emitido" : "recibido",
            doc_number: isTracked ? docNumber : null,
            doc_type: isTracked ? "C" : null,
            ...patch,
        })
        .select()
        .single();

    if (error) {
        await supabase.storage.from(BUCKET).remove([path]);
        throw error;
    }
    return data;
}

/**
 * Sube un documento aportado por el cliente (justificante de la transferencia).
 */
export async function uploadReceivedDocument({ teamId, eventId, kind, file, userId = null, notes = null }) {
    assertFile(file);
    const supabase = getSupabaseClient();

    const path = buildPath(eventId, kind, file);
    const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });

    if (upErr) {
        console.error("[eventDocuments] uploadReceivedDocument:", upErr);
        throw new Error("Error subiendo el archivo");
    }

    const { data, error } = await supabase
        .from(TABLE)
        .insert({
            team_id: teamId,
            event_id: eventId,
            kind,
            origin: "recibido",
            status: "recibido",
            file_path: path,
            file_name: file.name || null,
            file_size: file.size || null,
            uploaded_at: new Date().toISOString(),
            uploaded_by: userId,
            notes,
        })
        .select()
        .single();

    if (error) {
        await supabase.storage.from(BUCKET).remove([path]);
        throw error;
    }
    return data;
}

// ─── Acceso y borrado ─────────────────────────────────────────────────────────

/**
 * URL temporal para ver o descargar un documento del bucket privado.
 */
export async function getDocumentSignedUrl(path, expiresIn = 3600) {
    if (!path) return null;
    const supabase = getSupabaseClient();

    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, expiresIn);
    if (error) {
        console.error("[eventDocuments] getDocumentSignedUrl:", error);
        return null;
    }
    return data.signedUrl;
}

/**
 * Elimina un documento. En los emitidos se conserva la fila con el numero
 * correlativo (pasa a 'anulado'): borrarla dejaria un hueco injustificado en
 * la numeracion. En los recibidos se borra la fila entera.
 */
export async function deleteEventDocument(doc) {
    if (!doc?.id) return;
    const supabase = getSupabaseClient();

    if (doc.file_path) {
        const { error } = await supabase.storage.from(BUCKET).remove([doc.file_path]);
        if (error) console.warn("[eventDocuments] no se pudo borrar el fichero:", error);
    }

    if (doc.origin === "emitido") {
        const { error } = await supabase
            .from(TABLE)
            .update({
                status: "anulado",
                file_path: null,
                file_name: null,
                file_size: null,
                uploaded_at: null,
                uploaded_by: null,
            })
            .eq("id", doc.id);
        if (error) throw error;
        return;
    }

    const { error } = await supabase.from(TABLE).delete().eq("id", doc.id);
    if (error) throw error;
}
